import { useEffect, useRef } from 'react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import type { AnalysisJob } from '@/types/scriptAnalysis';
import { STAGE_LABELS } from '@/types/scriptAnalysis';
import { useContinueAnalysis } from '@/hooks/useScriptAnalysis';

interface AnalysisProgressProps {
  job: AnalysisJob;
  onCancel: () => void;
}

// How long to wait without progress before auto-continuing (ms)
const STALL_TIMEOUT = 45_000; // 45 seconds

export function AnalysisProgress({ job, onCancel }: AnalysisProgressProps) {
  const continueAnalysis = useContinueAnalysis();
  const continuingRef = useRef(false);
  const maxProgressRef = useRef(job.progress);
  const lastProgressChangeRef = useRef(Date.now());
  const lastProgressValueRef = useRef(job.progress);
  const stallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track max progress monotonically
  if (job.progress > maxProgressRef.current) {
    maxProgressRef.current = job.progress;
  }
  const displayProgress = maxProgressRef.current;

  // Track when progress last changed
  if (job.progress !== lastProgressValueRef.current) {
    lastProgressValueRef.current = job.progress;
    lastProgressChangeRef.current = Date.now();
  }

  // NOTE: El backend ahora auto-reencola con EdgeRuntime.waitUntil + selfInvokeContinue
  // (analyze-script desde 2026-05-28). El frontend ya NO dispara continue manualmente
  // cuando ve needs_continue. Solo monitoriza por si hubiera stall real (siguiente useEffect).

  // Detect stalled 'processing' jobs — if no progress change for STALL_TIMEOUT,
  // the Edge Function likely timed out silently. Trigger a single continue.
  useEffect(() => {
    // Only monitor during breakdown phase (where timeouts happen)
    if (job.status !== 'processing') return;
    if (job.stage !== 'breaking_down_scenes') return;
    if (job.progress <= 0 || job.progress >= 85) return;
    if (continuingRef.current) return;

    // Clear previous timer
    if (stallTimerRef.current) clearTimeout(stallTimerRef.current);

    stallTimerRef.current = setTimeout(() => {
      // Check that progress hasn't changed since we set the timer
      const timeSinceChange = Date.now() - lastProgressChangeRef.current;
      if (timeSinceChange >= STALL_TIMEOUT && !continuingRef.current) {
        console.log(`[stall-detect] No progress for ${Math.round(timeSinceChange / 1000)}s at ${job.progress}%, triggering continue...`);
        continuingRef.current = true;
        continueAnalysis.mutate(job.id, {
          onSettled: () => {
            setTimeout(() => { continuingRef.current = false; }, 5000);
          },
        });
      }
    }, STALL_TIMEOUT);

    return () => {
      if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
    };
  }, [job.status, job.stage, job.progress, job.id]);

  const isNeedsContinue = job.status === 'needs_continue';
  const stageLabel = job.stage === 'breaking_down_scenes' && job.total_scenes
    ? `Desglosando escenas... ${job.scenes_processed}/${job.total_scenes}`
    : STAGE_LABELS[job.stage] || job.stage;

  return (
    <div className="space-y-6">
      <p className="text-sm font-medium">
        Analizando: {job.file_name}
      </p>

      <div className="space-y-2">
        <Progress value={displayProgress} />
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground" aria-live="polite">
            {isNeedsContinue ? (
              <span className="text-blue-700 dark:text-blue-400">
                Continuando automáticamente... {stageLabel}
              </span>
            ) : (
              stageLabel
            )}
          </p>
          <span className="text-sm text-muted-foreground">{displayProgress}%</span>
        </div>
        {isNeedsContinue && job.error_message && (
          <p className="text-xs text-pelip-text-faint">{job.error_message}</p>
        )}
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-200">
        ☕ Un largometraje tarda <strong>~15-20 min</strong>. <strong>Puedes cerrar y volver cuando quieras</strong>: el análisis sigue en segundo plano y estará aquí al terminar. Tómate un café.
      </div>

      <div className="flex gap-2">
        {/* Botón fallback manual · sólo si el job lleva >2 min sin progreso (stall real). */}
        {(() => {
          const secondsSinceProgress = (Date.now() - lastProgressChangeRef.current) / 1000;
          const showFallback = isNeedsContinue || (job.status === 'processing' && secondsSinceProgress > 120);
          if (!showFallback) return null;
          return (
            <Button
              variant="outline"
              size="sm"
              onClick={() => continueAnalysis.mutate(job.id)}
              disabled={continueAnalysis.isPending}
              title="El sistema reanuda automáticamente. Sólo pulsa si lleva mucho rato sin avanzar."
            >
              {continueAnalysis.isPending ? 'Reanudando...' : 'Forzar reanudación'}
            </Button>
          );
        })()}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline"
              className="text-destructive border-destructive"
            >
              Cancelar análisis
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancelar análisis</AlertDialogTitle>
              <AlertDialogDescription>
                Se descartarán todos los resultados parciales. ¿Deseas continuar?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Volver</AlertDialogCancel>
              <AlertDialogAction onClick={onCancel}>
                Sí, cancelar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
